/**
 * The chromeless asset route: `/view/<kind>/<id>` for a dashboard or an
 * application rendered without the portal shell.
 *
 * The path carries no org segment on purpose. This is the exact URL a future
 * shared link would widen access to, so link sharing stays an access change
 * rather than a second surface; baking a tenant/org assumption into the path
 * here would make that a rewrite.
 */

export type ChromelessAssetKind = "dashboard" | "application";

const CHROMELESS_KINDS: readonly ChromelessAssetKind[] = ["dashboard", "application"];

/**
 * Parse a location pathname into the chromeless asset it addresses, or null
 * when it is not a well-formed chromeless route. Never throws: a hand-edited or
 * malformed URL (unknown kind, missing/empty id, trailing or extra segment,
 * broken percent-encoding) resolves to null so the router can fall through.
 */
export function parseChromelessAsset(
  pathname: string,
): { kind: ChromelessAssetKind; id: string } | null {
  // Exactly `/view/<kind>/<id>`: the leading "" is the root slash, then three
  // segments and nothing after. A trailing slash or extra segment splits to a
  // different length and is rejected rather than collapsed.
  const parts = pathname.split("/");
  if (parts.length !== 4 || parts[0] !== "" || parts[1] !== "view") return null;
  const kind = parts[2];
  if (!isChromelessKind(kind)) return null;
  const id = decodeSegment(parts[3]);
  if (id === undefined || id.length === 0) return null;
  return { kind, id };
}

/** The chromeless route for an asset, with the id percent-encoded for the URL. */
export function chromelessAssetPath(kind: ChromelessAssetKind, id: string): string {
  return `/view/${kind}/${encodeURIComponent(id)}`;
}

function isChromelessKind(value: string): value is ChromelessAssetKind {
  return (CHROMELESS_KINDS as readonly string[]).includes(value);
}

/** Decode one path segment, returning undefined when its percent-encoding is malformed. */
function decodeSegment(segment: string): string | undefined {
  try {
    return decodeURIComponent(segment);
  } catch {
    return undefined;
  }
}
